<script lang="ts">
	import type { RestaurantSemiStatic } from "$lib/def/landing";
	import type { Cache } from "$lib/serv/core/cache";
	import type { Menu } from "proto/menu";
	import type { ValueFromArray } from "rxjs";
	
	import { KEY_FOOD_ITEM } from "$lib/serv/food-landing";
	import { findRealItem, isBOGOF } from "$lib/ui/home/food/menu/Restaurant.svelte";
	import { query } from "$lib/util/url";
	import { makeRestPDPSlug } from "../food/menu/server";

	import ItemTile from "$lib/ui/home/shop/ItemTile.svelte";
	import SectionedListView, { KEY_SECTION, type SectionedList } from "$lib/ui/layout/SectionedListView.svelte";
	import Header from "$lib/ui/shared/Header.svelte";

	// TODO: Closing PDP on a section page takes user back to full menu
	type Section = ValueFromArray<Menu["sections"]>;
	type Item = ValueFromArray<Section["items"]>;

	export let storeId: RestaurantSemiStatic["id"];
	export let storeName: RestaurantSemiStatic["name"];
	export let cache: Cache<Menu>;
	export let sectionId: Section["id"] | null = null;
	export let pageUrl: URL;
	export let showSeeAllBtn = true;

	class MenuWrapper implements SectionedList<
		Menu, Section, Section["id"], Item
	> {
		getSections(m?: Menu): Section[] | undefined {
			return m?.sections;
		}

		getSection(id: Section["id"], m: Menu | undefined) {
			return m?.sections.find((s) => s.id === id);
		}

		getSectionId(section: Section) {
			return section.id;
		}

		getSectionTitle(section: Section) {
			return section.name;
		}

		getSectionUrl(section: Section) {
			return `${pageUrl.pathname}?${query(pageUrl).set(KEY_SECTION, section.id)}`;
		}

		getItems(section: Section) {
			return section.items;
		}

		getItemId(item: Item) {
			return item.id;
		}
	}

	/**
		Convert `i` to type `Item`.  This is handy in the HTML template, when we want to use the
		variables given back to us by a <slot>.
	*/
	function retypeItem(i: unknown) {
		return i as Item;
	}

	/**
	 * TODO: don't duplicate with shops search
	 * @param item
	 * @param sectionId
	 */
	function getFoodItemLink(item: Item, sectionId: Section["id"]) {
		if (!cache?.payload) return;

		const menu = cache.payload;
		let realItem: Item;
		if (isBOGOF(item)) {
			const realItemDetails = findRealItem(item, menu);
			if (!realItemDetails) return;
			realItem = realItemDetails;
			sectionId = realItemDetails.section_id;
		} else
			realItem = item;
		if (typeof realItem === "undefined") return "";
		
		return query(pageUrl).set(KEY_FOOD_ITEM, makeRestPDPSlug(realItem.id, sectionId, menu.id, storeId)).toUrl();
	}

</script>

{#if !sectionId}
	<Header
		back={`${pageUrl.pathname}?${query(pageUrl).remove(KEY_SECTION)}`}
		name={`${storeName}`}
	/>
{/if}
<SectionedListView
	{cache}
	listWrapper={new MenuWrapper()}
	{sectionId}
	{showSeeAllBtn}
>
	<!-- TODO: Test ID -->
	<!-- TODO: `retype()` not ideal in the slightest; fix -->
	{#snippet sectionChildren({ item, sectionId})}
		<a
			href={getFoodItemLink(retypeItem(item), sectionId)}
			class="item mobile medium-gap flex-nowrap align-start swimlane-menu-item"
			data-testid={`item_${retypeItem(item).id}_`}
			data-sveltekit-noscroll>
			<ItemTile item={retypeItem(item)} />
		</a>
	{/snippet}
</SectionedListView>
